import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import SuperadminArchivedHeader, { useShowArchived } from "../components/SuperadminArchivedHeader";
import {
  ApiError,
  api,
  type PlatformPaymentSettings,
  type TournamentFeeRow,
} from "../lib/api";
import { formatMoney } from "../lib/money";
import { feeTotals } from "../lib/superadminFeeTotals";
import {
  SHOW_ARCHIVED_KEYS,
  isArchivedTournament,
  splitArchived,
  superadminStatusLabel,
} from "../lib/superadminArchived";

interface FeeDraft {
  platform_fee_amount: string;
  admin_fee_amount: string;
  admin_fee_status: string;
  admin_fee_notes: string;
  manual_payments_allowed: boolean;
}

const ADMIN_FEE_STATUSES: Array<{ value: string; label: string }> = [
  { value: "not_applicable", label: "No aplica" },
  { value: "pending", label: "Pendiente" },
  { value: "invoiced", label: "Facturado" },
  { value: "paid", label: "Pagado" },
  { value: "waived", label: "Condonado" },
];

function draftFromRow(row: TournamentFeeRow): FeeDraft {
  return {
    platform_fee_amount: row.platform_fee_amount,
    admin_fee_amount: row.admin_fee_amount,
    admin_fee_status: row.admin_fee_status,
    admin_fee_notes: row.admin_fee_notes ?? "",
    manual_payments_allowed: row.manual_payments_allowed,
  };
}

/**
 * Panel central de fees (paquete de gobierno): el superadmin administra el
 * fee de plataforma y el costo admin de TODOS los torneos desde una sola
 * tabla, sin entrar torneo por torneo. El default global de arriba es el que
 * HEREDAN los torneos nuevos al crearse.
 */
export default function SuperadminPlatformFeesPage() {
  const { token } = useAuth();
  const [settings, setSettings] = useState<PlatformPaymentSettings | null>(null);
  const [rows, setRows] = useState<TournamentFeeRow[]>([]);
  const [drafts, setDrafts] = useState<Record<number, FeeDraft>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [defaultFee, setDefaultFee] = useState("");
  const [defaultActive, setDefaultActive] = useState(true);
  const [savingDefault, setSavingDefault] = useState(false);
  const [statusChip, setStatusChip] = useState<string>("all");
  // Eduardo 2026-10-03: los archivados (lápidas de gobierno incluidas) se
  // ocultan por default. Solo se filtra al pintar: los cambios sin guardar
  // viven en `drafts` y sobreviven a ocultar/mostrar. La casilla NUNCA
  // recarga la lista (load() borraría los cambios).
  const [showArchived, setShowArchived] = useShowArchived(SHOW_ARCHIVED_KEYS.fees);
  const { visible: visibleRows, archivedCount, hiddenCount } = splitArchived(rows, showArchived);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.payments.tournamentFees(token);
      setSettings(response.data.settings);
      setRows(response.data.tournaments);
      setDrafts({});
      setDefaultFee(response.data.settings.platform_fee_fixed_amount);
      setDefaultActive(response.data.settings.is_active);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || "No pudimos cargar los fees."
          : "Error de red. Intenta otra vez.",
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  function draftFor(row: TournamentFeeRow): FeeDraft {
    return drafts[row.tournament_id] ?? draftFromRow(row);
  }

  function setDraftField(row: TournamentFeeRow, patch: Partial<FeeDraft>) {
    setDrafts((prev) => ({
      ...prev,
      [row.tournament_id]: { ...draftFor(row), ...patch },
    }));
  }

  function rowDirty(row: TournamentFeeRow): boolean {
    const draft = drafts[row.tournament_id];
    if (!draft) return false;
    const base = draftFromRow(row);
    return (
      draft.platform_fee_amount !== base.platform_fee_amount ||
      draft.admin_fee_amount !== base.admin_fee_amount ||
      draft.admin_fee_status !== base.admin_fee_status ||
      draft.admin_fee_notes !== base.admin_fee_notes ||
      draft.manual_payments_allowed !== base.manual_payments_allowed
    );
  }

  async function handleSaveRow(row: TournamentFeeRow) {
    if (!token) return;
    const draft = draftFor(row);
    setSavingId(row.tournament_id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.payments.updateTournamentFees(token, row.tournament_id, {
        platform_fee_amount: draft.platform_fee_amount.trim() || "0",
        admin_fee_amount: draft.admin_fee_amount.trim() || "0",
        admin_fee_status: draft.admin_fee_status,
        admin_fee_notes: draft.admin_fee_notes.trim() || null,
        manual_payments_allowed: draft.manual_payments_allowed,
      });
      const updated = response.data.tournament;
      setRows((prev) =>
        prev.map((item) => (item.tournament_id === updated.tournament_id ? updated : item)),
      );
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[row.tournament_id];
        return next;
      });
      setMessage(
        `"${updated.name}": fees guardados. Órdenes pendientes actualizadas; las pagadas conservan su histórico.`,
      );
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || "No se pudo guardar."
          : "Error de red. Intenta otra vez.",
      );
    } finally {
      setSavingId(null);
    }
  }

  async function handleSaveDefault() {
    if (!token || !settings) return;
    setSavingDefault(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api.payments.updatePlatformSettings(token, {
        currency_default: settings.currency_default,
        platform_fee_type: "fixed",
        platform_fee_fixed_amount: defaultFee.trim() || "0",
        platform_fee_percent_bps: settings.platform_fee_percent_bps,
        minimum_platform_fee_amount: settings.minimum_platform_fee_amount,
        is_active: defaultActive,
      });
      setSettings(response.data.settings);
      setMessage(
        "Default guardado. Aplica a torneos NUEVOS; los existentes se ajustan aquí abajo, fila por fila.",
      );
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || "No se pudo guardar el default."
          : "Error de red. Intenta otra vez.",
      );
    } finally {
      setSavingDefault(false);
    }
  }

  // Archivados ocultos con cambios sin guardar: se avisa en la página para
  // que no se olviden (no se bloquea la casilla ni se descarta nada).
  const hiddenDirty = showArchived
    ? 0
    : rows.filter((row) => isArchivedTournament(row) && rowDirty(row)).length;

  // Rediseño 2026-10-09: totales del encabezado azul y chips por estado.
  const totals = feeTotals(rows);
  const statusCounts = ADMIN_FEE_STATUSES.reduce<Record<string, number>>((acc, option) => {
    acc[option.value] = visibleRows.filter((row) => row.admin_fee_status === option.value).length;
    return acc;
  }, {});
  const tableRows =
    statusChip === "all" ? visibleRows : visibleRows.filter((row) => row.admin_fee_status === statusChip);

  return (
    <div className="adm-players sa-fees">
      <header className="adm-players__head">
        <div>
          <span className="adm-players__eyebrow">Superadmin · cobro de la plataforma</span>
          <h1 className="adm-players__title">Fees de plataforma</h1>
          <p className="muted sa-section-sub">
            El fee por jugador de cada torneo, en una sola tabla. Cambiarlo recalcula las órdenes pendientes; los
            pagos confirmados conservan su histórico.
          </p>
        </div>
        <div className="adm-players__head-actions">
          <Link className="adm-players__btn adm-players__btn--secondary sa-link-btn" to="/superadmin/fees-organizadores">
            Deuda de organizadores
          </Link>
          <Link className="adm-players__btn adm-players__btn--secondary sa-link-btn" to="/superadmin">
            ← Volver
          </Link>
        </div>
      </header>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {message && <p className="form-message">{message}</p>}

      <section className="adm-fin__summary sa-fees__summary" aria-label="Resumen de fees">
        <div className="adm-fin__net">
          <span className="adm-fin__label">Acumulado</span>
          <span className="adm-fin__net-value">{formatMoney(totals.accrued, totals.currency)}</span>
          <small>todos los torneos</small>
        </div>
        <div className="adm-fin__metrics">
          <div className="adm-fin__metric adm-fin__metric--lime">
            <span className="adm-fin__label">Pendiente</span>
            <strong>{formatMoney(totals.pending, totals.currency)}</strong>
          </div>
          <div className="adm-fin__metric">
            <span className="adm-fin__label">Pagado</span>
            <strong>{formatMoney(totals.paid, totals.currency)}</strong>
          </div>
          <div className="adm-fin__metric sa-fees__default">
            <span className="adm-fin__label">Fee para torneos nuevos</span>
            <div className="sa-fees__default-row">
              <input
                type="text"
                aria-label="Fee por jugador para torneos nuevos (MXN)"
                value={defaultFee}
                onChange={(event) => setDefaultFee(event.target.value)}
                disabled={savingDefault}
              />
              <label className="sa-fees__default-active">
                <input
                  type="checkbox"
                  checked={defaultActive}
                  onChange={(event) => setDefaultActive(event.target.checked)}
                  disabled={savingDefault}
                />
                <span>Activo</span>
              </label>
              <button
                className="btn btn--compact sa-fees__save-default"
                type="button"
                onClick={() => void handleSaveDefault()}
                disabled={savingDefault || !settings}
              >
                {savingDefault ? "Guardando…" : "Guardar"}
              </button>
            </div>
            <small>Inactivo = los torneos nuevos nacen con fee $0.</small>
          </div>
        </div>
      </section>

      <section className="adm-players__panel">
        <SuperadminArchivedHeader
          shownCount={visibleRows.length}
          archivedCount={archivedCount}
          hiddenCount={hiddenCount}
          showArchived={showArchived}
          onToggle={setShowArchived}
        />
        <div className="adm-players__chips" role="group" aria-label="Filtrar por estado">
          <button
            type="button"
            className={`adm-players__filter${statusChip === "all" ? " is-active" : ""}`}
            aria-pressed={statusChip === "all"}
            onClick={() => setStatusChip("all")}
          >
            Todos <span className="adm-fin__chip-count">{visibleRows.length}</span>
          </button>
          {ADMIN_FEE_STATUSES.filter((option) => option.value !== "not_applicable").map((option) => (
            <button
              key={option.value}
              type="button"
              className={`adm-players__filter${statusChip === option.value ? " is-active" : ""}`}
              aria-pressed={statusChip === option.value}
              onClick={() => setStatusChip(option.value)}
            >
              {option.label} <span className="adm-fin__chip-count">{statusCounts[option.value] ?? 0}</span>
            </button>
          ))}
        </div>
        {hiddenDirty > 0 ? (
          <p className="alert alert--warning gov-archived-warning" data-no-toast>
            {hiddenDirty === 1
              ? "Tienes cambios sin guardar en 1 torneo archivado. Marca «Mostrar archivados» para verlo y guardarlo."
              : `Tienes cambios sin guardar en ${hiddenDirty} torneos archivados. Marca «Mostrar archivados» para verlos y guardarlos.`}
          </p>
        ) : null}
        {loading ? (
          <p className="muted">Cargando…</p>
        ) : rows.length === 0 ? (
          <p className="muted">No hay torneos en la plataforma.</p>
        ) : visibleRows.length === 0 ? (
          <p className="muted" data-no-toast>
            Todos los torneos están archivados. Marca «Mostrar archivados» para verlos.
          </p>
        ) : tableRows.length === 0 ? (
          <p className="muted">Ningún torneo en este estado.</p>
        ) : (
          <div className="adm-players__table-shell adm-fin__table-wrap">
            <table className="data-table adm-players__table adm-players__table--wide adm-fin__table sa-fees__table">
              <colgroup>
                <col className="sa-fees__col-name" />
                <col className="sa-fees__col-entry" />
                <col className="sa-fees__col-fee" />
                <col className="sa-fees__col-receipts" />
                <col className="sa-fees__col-accrued" />
                <col className="sa-fees__col-status" />
              </colgroup>
              <thead>
                <tr>
                  <th>Torneo</th>
                  <th>Inscripción</th>
                  <th>Fee por inscrito</th>
                  <th>Comprobantes</th>
                  <th>Acumulado</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => {
                  const draft = draftFor(row);
                  const busy = savingId === row.tournament_id;
                  return (
                    <tr key={row.tournament_id}>
                      <td className="data-table__cell--name" data-label="Torneo">
                        <Link className="adm-players__name" to={`/admin/tournaments/${row.tournament_id}/setup/registrations`}>
                          {row.name}
                        </Link>
                        <small className="muted"> · {superadminStatusLabel(row.status)}</small>
                        <small className="muted sa-fees__orders">
                          {row.paid_orders} pagadas
                          {row.pending_orders > 0 ? ` · ${row.pending_orders} pend.` : ""}
                        </small>
                      </td>
                      <td className="data-table__cell--num" data-label="Inscripción">
                        {formatMoney(Number(row.entry_fee_amount), row.currency)}
                      </td>
                      <td data-label="Fee por inscrito">
                        <div className="sa-fees__fee">
                          <input
                            type="text"
                            className="data-table__input"
                            style={{ width: "6rem" }}
                            aria-label={`Fee de plataforma de ${row.name}`}
                            value={draft.platform_fee_amount}
                            onChange={(event) =>
                              setDraftField(row, { platform_fee_amount: event.target.value })
                            }
                            disabled={busy}
                          />
                          <button
                            className="btn btn--compact adm-players__edit"
                            type="button"
                            onClick={() => void handleSaveRow(row)}
                            disabled={busy || (!rowDirty(row) && !row.fee_review_needed)}
                          >
                            {busy ? "Guardando…" : "Guardar"}
                          </button>
                        </div>
                        {/* Copia de otro torneo: el default no cabía en sus
                            costos y se quedó el fee del original. Guardar
                            (aunque no cambie) lo da por revisado. */}
                        {row.fee_review_needed ? (
                          <small className="superadmin-fee-review" data-no-toast>
                            Por revisar: es una copia y el fee default era mayor que alguno de sus costos.
                          </small>
                        ) : null}
                      </td>
                      <td data-label="Comprobantes">
                        {/* Candado de comprobantes: solo desde aquí se
                            permite cobrar manual (casos específicos). */}
                        <label
                          className={`adm-fin__status sa-fees__receipts${draft.manual_payments_allowed ? " adm-fin__status--active" : ""}`}
                          title="Permitir a este torneo cobrar con comprobantes de pago (por fuera de Mercado Pago)"
                        >
                          <input
                            type="checkbox"
                            aria-label={`Permitir comprobantes en ${row.name}`}
                            checked={draft.manual_payments_allowed}
                            onChange={(event) =>
                              setDraftField(row, { manual_payments_allowed: event.target.checked })
                            }
                            disabled={busy}
                          />{" "}
                          {draft.manual_payments_allowed ? "Permitidos" : "Bloqueados"}
                        </label>
                      </td>
                      <td className="data-table__cell--num" data-label="Acumulado">
                        <strong>{formatMoney(Number(row.accrued_platform_fee), row.currency)}</strong>
                      </td>
                      <td data-label="Estado">
                        <select
                          className="adm-tourn__select sa-fees__status"
                          aria-label={`Estado del costo admin de ${row.name}`}
                          value={draft.admin_fee_status}
                          onChange={(event) =>
                            setDraftField(row, { admin_fee_status: event.target.value })
                          }
                          disabled={busy}
                        >
                          {ADMIN_FEE_STATUSES.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <label className="sa-fees__admin-cost">
                          <small>Costo admin</small>
                          <input
                            type="text"
                            className="data-table__input"
                            style={{ width: "6rem" }}
                            aria-label={`Costo admin de ${row.name}`}
                            value={draft.admin_fee_amount}
                            onChange={(event) =>
                              setDraftField(row, { admin_fee_amount: event.target.value })
                            }
                            disabled={busy}
                          />
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
